'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { capitalizeLeading, resolveProposalCategory } from '@/config/proposal-display';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { ProposalOutcomeItem } from './types';
export interface ProposalOutcomeCountProps {
  /** Pubkey of the parent proposal — used as the OptimizedImage entity key for outcome thumbnails. */
  proposalPubkey: string;
  category: string;
  outcomes: ProposalOutcomeItem[];
  categoryIcon?: IconSvgElement;
}
export function ProposalOutcomeCount(t0) {
  const $ = _c(40);
  const {
    proposalPubkey,
    category,
    outcomes,
    categoryIcon
  } = t0;
  const t = useTranslations("launchpad.feed");
  const tCategories = useTranslations("categories");
  let t1;
  if ($[0] !== category || $[1] !== tCategories) {
    const resolved = resolveProposalCategory(category);
    t1 = resolved !== null ? tCategories(resolved.id as Parameters<typeof tCategories>[0]) : capitalizeLeading(category);
    $[0] = category;
    $[1] = tCategories;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const categoryLabel = t1;
  const t2 = outcomes.length > 0 ? 0 : -1;
  let t3;
  if ($[3] !== outcomes.length || $[4] !== t) {
    t3 = t("outcomeCount", {
      count: outcomes.length
    });
    $[3] = outcomes.length;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="text-caption text-foreground-tertiary outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand rounded-item" tabIndex={t2} data-testid="proposal-outcome-count-trigger">{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const countNode = t4;
  let t5;
  if ($[9] !== outcomes) {
    t5 = outcomes.map(_temp);
    $[9] = outcomes;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const colorClasses = useOutcomeColor(t5);
  let countElement = countNode;
  if (outcomes.length > 0) {
    let t6;
    if ($[11] !== t) {
      t6 = t("outcomesTooltipTitle");
      $[11] = t;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t6) {
      t7 = <span className="text-caption font-medium opacity-70">{t6}</span>;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== colorClasses || $[16] !== outcomes || $[17] !== proposalPubkey) {
      let t9;
      if ($[19] !== colorClasses || $[20] !== proposalPubkey) {
        t9 = (outcome, idx) => {
          const hue = colorClasses[idx]?.hue ?? "blue";
          const resolvedImageUrl = outcome.imageNsfw === false && outcome.imageUrl !== "" && outcome.imageUrl !== undefined ? outcome.imageUrl : null;
          return <li key={`${String(outcome.outcomeIndex)}-${outcome.label}`} className="flex items-center gap-2"><OptimizedImage variant="thumbnail" entity={{
              kind: "outcome",
              marketPubkey: proposalPubkey,
              outcomeIndex: outcome.outcomeIndex,
              hue,
              imageUrl: resolvedImageUrl,
              nsfw: outcome.imageNsfw
            }} alt="" size="size-4" rounded="rounded-full" preview={false} /><span>{outcome.label}</span></li>;
        };
        $[19] = colorClasses;
        $[20] = proposalPubkey;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      t8 = outcomes.map(t9);
      $[15] = colorClasses;
      $[16] = outcomes;
      $[17] = proposalPubkey;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[22] !== t8) {
      t9 = <ul className="flex flex-col gap-1.5">{t8}</ul>;
      $[22] = t8;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t7 || $[25] !== t9) {
      t10 = <div className="flex flex-col gap-1.5">{t7}{t9}</div>;
      $[24] = t7;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    const tooltipContent = t10;
    let t11;
    if ($[27] !== countNode || $[28] !== tooltipContent) {
      t11 = <AppTooltip content={tooltipContent} contentClassName="py-2.5">{countNode}</AppTooltip>;
      $[27] = countNode;
      $[28] = tooltipContent;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    countElement = t11;
  }
  let t6;
  if ($[30] !== categoryIcon) {
    t6 = categoryIcon ? <Icon icon={categoryIcon} aria-hidden={true} className="size-3.5 shrink-0" /> : null;
    $[30] = categoryIcon;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== categoryLabel) {
    t7 = <span className="truncate">{categoryLabel}</span>;
    $[32] = categoryLabel;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== t6 || $[35] !== t7) {
    t8 = <span className="flex min-w-0 items-center gap-1 text-body font-medium text-foreground-primary">{t6}{t7}</span>;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== countElement || $[38] !== t8) {
    t9 = <div className="flex flex-col gap-0.5 leading-tight min-w-0">{t8}{countElement}</div>;
    $[37] = countElement;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.outcomeIndex
  };
}